import { Hand, Meld, Suit, Tile } from './types';

const findNOfAKinds = (
  n: number,
  groups: Tile[][],
  existingMelds: Meld[],
): Meld[] => {
  const foundNOfKinds: Tile[][] = [];
  const usedTiles = existingMelds.map((meld) => meld.tiles);

  for (let group of groups) {
    const potentialNOfKinds: Tile[][] = [];

    for (let i = 0; i < group.length; i++) {
      const tile = group[i];
      const potentialNOfKind: Tile[] = [tile];

      for (let j = 0; j < group.length; j++) {
        const nextTile = group[j];

        if (potentialNOfKind.length === n) {
          break;
        }

        if (
          tile.id === nextTile.id ||
          potentialNOfKinds.some((nOfKinds) => {
            const nOfKindsIds = nOfKinds.map((tile) => tile.id);
            return (
              nOfKindsIds.includes(tile.id) || nOfKindsIds.includes(nextTile.id)
            );
          }) ||
          usedTiles.some((usedTile) => {
            const usedTileIds = usedTile.map((tile) => tile.id);
            return (
              usedTileIds.includes(tile.id) || usedTileIds.includes(nextTile.id)
            );
          })
        ) {
          continue;
        }

        if (
          potentialNOfKind.find((tile) => tile.value === nextTile.value) &&
          !potentialNOfKind.some((tile) => tile.id === nextTile.id)
        ) {
          potentialNOfKind.push(nextTile);
          j = 0; // Re-run over all tiles again
        }
      }

      if (potentialNOfKind.length === n) {
        potentialNOfKinds.push(potentialNOfKind);
      }
    }

    foundNOfKinds.push(...potentialNOfKinds);
  }

  return foundNOfKinds.map((nOfKind) => ({
    tiles: nOfKind,
    Suit: nOfKind[0].suit as Suit,
    isOpen: nOfKind.find((tile) => tile.open)?.open || false,
  }));
};

const findRuns = (tileGroups: Tile[][]): Meld[] => {
  const runs: Tile[][] = [];
  for (let group of tileGroups) {
    const potentialRuns: Tile[][] = [];

    for (let i = 0; i < group.length; i++) {
      const tile = group[i];
      const potentialRun: Tile[] = [tile];

      for (let j = 0; j < group.length; j++) {
        const nextTile = group[j];

        if (potentialRun.length === 3) {
          break;
        }

        if (
          tile.id === nextTile.id ||
          potentialRuns.some((run) => {
            const runIds = run.map((tile) => tile.id);
            return runIds.includes(tile.id) || runIds.includes(nextTile.id);
          })
        ) {
          continue;
        }

        if (
          potentialRun.find(
            (tile) =>
              (parseInt(tile.value) === parseInt(nextTile.value) + 1 ||
                parseInt(tile.value) === parseInt(nextTile.value) - 1) &&
              !potentialRun.some((tile) => tile.value === nextTile.value),
          )
        ) {
          potentialRun.push(nextTile);
          j = 0; // Re-run over all tiles again
        }
      }

      if (potentialRun.length === 3) {
        potentialRuns.push(potentialRun);
      }
    }

    runs.push(...potentialRuns);
  }

  return runs.map((run) => ({
    tiles: run,
    Suit: run[0].suit as Suit,
    isOpen: run.find((tile) => tile.open)?.open || false,
  }));
};

export const detectMelds = (
  hand: Hand,
): {
  runs: Meld[];
  fourOfAKinds: Meld[];
  threeOfAKinds: Meld[];
  pairs: Meld[];
  unusedTiles: Tile[];
} => {
  const souTiles = hand
    .filter((tile) => tile.suit === 's')
    .sort((a, b) => parseInt(a.value) - parseInt(b.value));
  const pinTiles = hand
    .filter((tile) => tile.suit === 'p')
    .sort((a, b) => parseInt(a.value) - parseInt(b.value));
  const manTiles = hand
    .filter((tile) => tile.suit === 'm')
    .sort((a, b) => parseInt(a.value) - parseInt(b.value));
  const honorTiles = hand
    .filter((tile) => tile.suit === 'z')
    .sort((a, b) => parseInt(a.value) - parseInt(b.value));

  const runs = findRuns([souTiles, pinTiles, manTiles]);

  const fourOfAKinds = findNOfAKinds(
    4,
    [souTiles, pinTiles, manTiles, honorTiles],
    [...runs],
  );

  const threeOfAKinds = findNOfAKinds(
    3,
    [souTiles, pinTiles, manTiles, honorTiles],
    [...runs, ...fourOfAKinds],
  );

  const pairs = findNOfAKinds(
    2,
    [souTiles, pinTiles, manTiles, honorTiles],
    [...runs, ...fourOfAKinds, ...threeOfAKinds],
  );

  const meldTileIds = [...runs, ...fourOfAKinds, ...threeOfAKinds, ...pairs]
    .flatMap((meld) => [...meld.tiles])
    .map((tile) => tile.id);

  return {
    runs,
    fourOfAKinds,
    threeOfAKinds,
    pairs,
    unusedTiles: hand.filter((tile) => !meldTileIds.includes(tile.id)),
  };
};
